import { unwrapResult } from '@reduxjs/toolkit';
import { Box } from '@wix/design-system';
import React, { FC, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useSkipFirstEffect } from '../../../chatbot/components/Field/useSkipFirstEffect';
import { Loader } from '../../../common/components/Loader';
import { homepageSlice } from '../../../common/reducers/homepage';
import {
  selectRecommendedArticles,
  selectRecommendedArticlesSection,
} from '../../../common/selectors/homepage';
import { selectLoadPageUrl } from '../../../common/selectors/meta';
import { useAppDispatch } from '../../../common/store';
import { loadRecommendedArticles } from '../../../common/thunks/recommendedArticles.thunk';
import { ArticlesListItem } from '../../../help-center/components/ArticlesListItem';
import { biHelpCenter } from '../../../util/bi';
import { SectionComponentProps } from '../types';
import { recommendedArticlesDataHooks } from './RecommendedArticles.dataHooks';

const ARTICLE_ITEM_HEIGHT = 36; // px
const DEFAULT_PAGE_SIZE = 5;

export const RecommendedArticles: FC<SectionComponentProps> = ({
  setSectionHidden,
}) => {
  const dispatch = useAppDispatch();
  const loadPageUrl = useSelector(selectLoadPageUrl);
  const recommendedArticlesSection = useSelector(
    selectRecommendedArticlesSection,
  );
  const pageSize = recommendedArticlesSection?.pageSize || DEFAULT_PAGE_SIZE;
  const { data, loading, error } = useSelector(selectRecommendedArticles);
  const recommendedArticles = recommendedArticlesSection?.articles || [];
  const skipFetch = recommendedArticles && recommendedArticles.length;

  const fetchRecommendedArticles = useCallback(
    async () =>
      dispatch(loadRecommendedArticles({ size: pageSize }))
        .then(unwrapResult)
        .then(({ articles, modelId }) => {
          setSectionHidden(!articles?.length);
          biHelpCenter.articleRecommendationVersion = modelId;
        })
        .catch(() => setSectionHidden(true)),
    [dispatch, pageSize, setSectionHidden],
  );

  const interactionLogger = useCallback((articleId: string, index: number) => {
    biHelpCenter.logOpenRecommendedArticleInHC(articleId, index);
  }, []);

  useEffect(() => {
    if (skipFetch) {
      return;
    }
    void fetchRecommendedArticles();

    return () => {
      // reset recommended articles on unmount to send correct state in BI(if recommended articles are not displayed)
      dispatch(homepageSlice.actions.resetRecommendedArticles());
    };
  }, [skipFetch]);

  useSkipFirstEffect(() => {
    // refresh recommended articles when user navigates throw the pages
    if (skipFetch) {
      return;
    }
    void fetchRecommendedArticles();
  }, [loadPageUrl, skipFetch]);

  if (loading || error || !data?.articles?.length) {
    return (
      <Box
        width="100%"
        padding="6px 0"
        align="center"
        dataHook={recommendedArticlesDataHooks.loader()}
      >
        <Loader
          height={`${ARTICLE_ITEM_HEIGHT * pageSize}px`}
          verticalAlign="middle"
        />
      </Box>
    );
  }

  return (
    <Box
      direction="vertical"
      width="100%"
      padding="6px 0"
      overflow="hidden"
      dataHook={recommendedArticlesDataHooks.container()}
    >
      {data.articles.map(({ id, title, titleKey }, index) => (
        <ArticlesListItem
          key={id}
          id={id}
          rowNumber={index}
          title={title}
          titleKey={titleKey}
          interactionLogger={interactionLogger}
          smallPaddings
          withChevronRight
          textSize="small"
          ellipsis
          dataHook={recommendedArticlesDataHooks.item({ id })}
        />
      ))}
    </Box>
  );
};
